import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Phone, ArrowLeft, Loader2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

export default function LoginPage() {
  const [step, setStep] = useState(1); // 1 = phone input, 2 = OTP input
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [error, setError] = useState('');
  
  const otpInputRefs = useRef([]);
  const { loginWithOTP, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  const from = location.state?.from?.pathname || '/';
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (isAuthenticated && user) {
      if (user.role === 'admin' || user.role === 'vendor') {
        navigate('/admin/dashboard', { replace: true });
      } else {
        navigate(from, { replace: true });
      }
    }
  }, [isAuthenticated, user, navigate, from]);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendTimer]);

  // Focus first OTP input when entering step 2
  useEffect(() => {
    if (step === 2 && otpInputRefs.current[0]) {
      otpInputRefs.current[0].focus();
    }
  }, [step]);

  const formatPhoneForDisplay = (phoneNumber) => {
    const cleaned = phoneNumber.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `+91 ${cleaned.slice(0, 5)} ${cleaned.slice(5)}`;
    }
    return phoneNumber;
  };

  const validatePhone = () => {
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length < 10) {
      setError('Please enter a valid 10-digit phone number');
      return false;
    }
    setError('');
    return true;
  };

  const handleSendOTP = async (e) => {
    e?.preventDefault();
    
    if (!validatePhone()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const cleanedPhone = phone.replace(/\D/g, '');
      const formattedPhone = cleanedPhone.startsWith('91') ? `+${cleanedPhone}` : `+91${cleanedPhone}`;
      
      const response = await axios.post(`${API_URL}/api/auth/send-otp`, {
        phone: formattedPhone
      });
      
      if (response.data.success) {
        toast.success('OTP sent to your phone!');
        setStep(2);
        setResendTimer(30);
      }
    } catch (err) {
      console.error('Send OTP error:', err);
      setError(err.response?.data?.detail || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOtpChange = (index, value) => {
    // Only allow digits
    if (value && !/^\d$/.test(value)) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');
    
    // Auto-focus next input
    if (value && index < 5) {
      otpInputRefs.current[index + 1]?.focus();
    }
    
    // Auto-submit when all digits entered
    if (value && index === 5) {
      const fullOtp = newOtp.join('');
      if (fullOtp.length === 6) {
        handleVerifyOTP(fullOtp);
      }
    }
  };

  const handleOtpKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus();
    }
  };

  const handleOtpPaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (pastedData) {
      const newOtp = pastedData.split('').concat(Array(6).fill('')).slice(0, 6);
      setOtp(newOtp);
      if (pastedData.length === 6) {
        handleVerifyOTP(pastedData);
      }
    }
  };

  const handleVerifyOTP = async (otpCode) => {
    const code = otpCode || otp.join('');
    
    if (code.length !== 6) {
      setError('Please enter a valid 6-digit OTP');
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      const cleanedPhone = phone.replace(/\D/g, '');
      const formattedPhone = cleanedPhone.startsWith('91') ? `+${cleanedPhone}` : `+91${cleanedPhone}`;
      
      const response = await axios.post(`${API_URL}/api/auth/verify-otp`, {
        phone: formattedPhone,
        code: code
      });
      
      if (response.data.success) {
        toast.success('Login successful!');
        loginWithOTP(response.data.access_token, response.data.user);
      }
    } catch (err) {
      console.error('Verify OTP error:', err);
      setError(err.response?.data?.detail || 'Invalid OTP. Please try again.');
      setOtp(['', '', '', '', '', '']);
      otpInputRefs.current[0]?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = () => {
    if (resendTimer === 0) {
      handleSendOTP();
    }
  };

  const handleBack = () => {
    setStep(1);
    setOtp(['', '', '', '', '', '']);
    setError('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-green-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="flex items-center justify-center space-x-3">
            <img 
              src="https://customer-assets.emergentagent.com/job_shopstack-46/artifacts/wfop58ec_image.png" 
              alt="Nammahut Logo" 
              className="h-16 w-16 object-contain"
            />
            <span className="text-3xl font-bold text-green-800">NAMMAHUT</span>
          </Link>
          <p className="text-green-700 mt-2">Our traditions come home</p>
        </div>

        <Card className="border-2 border-green-100 shadow-2xl overflow-hidden">
          {step === 1 ? (
            <>
              <CardHeader className="space-y-1 text-center bg-gradient-to-r from-green-50 to-green-100">
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                  <Phone className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold text-green-900">Welcome</CardTitle>
                <CardDescription className="text-green-700">
                  Enter your phone number to continue
                </CardDescription>
              </CardHeader>
              
              <CardContent className="p-6">
                <form onSubmit={handleSendOTP} className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Phone Number
                    </label>
                    <div className="flex">
                      <div className="flex items-center px-4 bg-gray-100 border-2 border-r-0 border-green-200 rounded-l-lg">
                        <span className="text-gray-600 font-medium">+91</span>
                      </div>
                      <Input
                        type="tel"
                        value={phone}
                        onChange={(e) => {
                          setPhone(e.target.value.replace(/\D/g, '').slice(0, 10));
                          setError('');
                        }}
                        className="flex-1 border-2 border-green-200 rounded-l-none focus:border-green-500 text-lg tracking-wider"
                        placeholder="98765 43210"
                        maxLength={10}
                        data-testid="phone-input"
                      />
                    </div>
                    {error && (
                      <p className="text-sm text-red-600" data-testid="phone-error">{error}</p>
                    )}
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-6 text-lg" 
                    disabled={loading || phone.length < 10}
                    data-testid="send-otp-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Sending OTP...
                      </>
                    ) : (
                      'Get OTP'
                    )}
                  </Button>
                </form>

                <p className="text-center text-xs text-gray-500 mt-6">
                  By continuing, you agree to our{' '}
                  <Link to="/terms" className="text-green-600 hover:underline">
                    Terms of Service
                  </Link>{' '}
                  and{' '}
                  <Link to="/privacy" className="text-green-600 hover:underline">
                    Privacy Policy
                  </Link>
                </p>
              </CardContent>
            </>
          ) : (
            <>
              <CardHeader className="space-y-1 text-center bg-gradient-to-r from-green-50 to-green-100">
                <button
                  onClick={handleBack}
                  className="absolute left-4 top-4 p-2 hover:bg-green-200 rounded-full transition-colors"
                  data-testid="back-button"
                >
                  <ArrowLeft className="w-5 h-5 text-green-700" />
                </button>
                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                  <ShieldCheck className="w-8 h-8 text-white" />
                </div>
                <CardTitle className="text-2xl font-bold text-green-900">Verify OTP</CardTitle>
                <CardDescription className="text-green-700">
                  Enter the 6-digit code sent to
                  <br />
                  <span className="font-semibold text-green-800">
                    {formatPhoneForDisplay(phone)}
                  </span>
                </CardDescription>
              </CardHeader>
              
              <CardContent className="p-6">
                <div className="space-y-6">
                  {/* OTP Input */}
                  <div className="flex justify-center gap-2" onPaste={handleOtpPaste}>
                    {otp.map((digit, index) => (
                      <Input
                        key={index}
                        ref={(el) => (otpInputRefs.current[index] = el)}
                        type="text"
                        inputMode="numeric"
                        maxLength={1}
                        value={digit}
                        onChange={(e) => handleOtpChange(index, e.target.value)}
                        onKeyDown={(e) => handleOtpKeyDown(index, e)}
                        className={`w-12 h-14 text-center text-2xl font-bold border-2 ${
                          error ? 'border-red-300' : 'border-green-200'
                        } focus:border-green-500 rounded-lg`}
                        data-testid={`otp-input-${index}`}
                      />
                    ))}
                  </div>

                  {error && (
                    <p className="text-sm text-red-600 text-center" data-testid="otp-error">
                      {error}
                    </p>
                  )}

                  <Button 
                    onClick={() => handleVerifyOTP()}
                    className="w-full bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white font-bold py-6 text-lg" 
                    disabled={loading || otp.join('').length !== 6}
                    data-testid="verify-otp-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Verifying...
                      </>
                    ) : (
                      'Verify & Login'
                    )}
                  </Button>

                  {/* Resend OTP */}
                  <div className="text-center">
                    <p className="text-sm text-gray-600">
                      Didn't receive the code?{' '}
                      {resendTimer > 0 ? (
                        <span className="text-gray-400">
                          Resend in {resendTimer}s
                        </span>
                      ) : (
                        <button
                          onClick={handleResendOTP}
                          className="text-green-600 font-medium hover:underline"
                          disabled={loading}
                          data-testid="resend-otp-button"
                        >
                          Resend OTP
                        </button>
                      )}
                    </p>
                  </div>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
